import { Route, Routes, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { Modal, message } from 'antd';
import { NavDropdown } from 'react-bootstrap';
import { GlobalStateProvider } from './components/GlobalStateProvider';
import UpdateNotification from './components/UpdateNotification';
import useSingleSignOn from './hooks/useSingleSignOn';
import websocketService from './services/websocketService';
import { getAccountTypeFromToken, getUserRoleFromToken, isUserAuthenticated, clearAuthData, performLogout } from './utils/auth';
import { logout as logoutAPI } from './services/api';
import HomePage from './pages/HomePage';
import AdminDashboard from './pages/AdminDashboard';
import UserDashboard from './pages/UserDashboard';
import UserManagement from './pages/UserManagement';
import IpAllocation from './pages/IpAllocation';
import TrafficQuery from './pages/TrafficQuery';
import Login from './pages/Login';
import RAMLogin from './pages/RAMLogin';
import UserIpPermissions from './pages/UserIpPermissions';
import ConnectionQuery from './pages/ConnectionQuery';
import AdminAttackQuery from './pages/AdminAttackQuery';
import UserAttackQuery from './pages/UserAttackQuery';
import IPPolicyAdjustment from './pages/IPPolicyAdjustment';
import IPPolicyAdjustmentUser from './pages/IPPolicyAdjustmentUser';
import HuBeiXiaoQiangTrafficQuery from './pages/HuBeiXiaoQiangTrafficQuery';
import UserHuBeiXiaoQiangTrafficQuery from './pages/UserHuBeiXiaoQiangTrafficQuery';
import ApplyAccount from './pages/ApplyAccount';
import GetSetMeal from './pages/GetSetMeal';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import HuBeiXiaoQiangAdminAttackQuery from './pages/HuBeiXiaoQiangAdminAttackQuery';
import UserConnectionQuery from './pages/UserConnectionQuery';
import LogsPage from './pages/LogPage';
import UserHomePage from './pages/UserHomePage';
import UserSettings from './pages/UserSettings';
import IpTowLookup from './pages/IpTowLookup';
import IpTowHistoryLookup from './pages/IpTowHistoryLookup';
import AllServersAttackQuery from './pages/AllServersAttackQuery';
import NoticeManagement from './pages/NoticeManagement';
import SystemVersionManagement from './pages/SystemVersionManagement';
import SystemUpdateLog from './pages/SystemUpdateLog';
import RAMAccountManagement from './pages/RAMAccountManagement';
import SessionManagement from './pages/SessionManagement';
import IPPermissionManagement from './pages/IPPermissionManagement';
import AdminBugReportManagement from './pages/AdminBugReportManagement';
import ApplicationRules from './pages/ApplicationRules';
import UserApplicationRules from './pages/UserApplicationRules';
import DynamicBlacklistManagement from './pages/DynamicBlacklistManagement';
import StaticWhitelistManagement from './pages/StaticWhitelistManagement';
import StaticBlacklistManagement from './pages/StaticBlacklistManagement';
import UserAutoProtection from './pages/UserAutoProtection';
import AdminAutoProtection from './pages/AdminAutoProtection';
import AdminWafManagement from './pages/AdminWafManagement';
import WafManagement from './pages/WafManagement';
import UserTickets from './pages/UserTickets';
import AdminTickets from './pages/AdminTickets';
import UserTicketDetail from './pages/UserTicketDetail';
import AdminTicketDetail from './pages/AdminTicketDetail';
import AdminFilterRuleQuery from './pages/AdminFilterRuleQuery';
import AdminApplyRuleQuery from './pages/AdminApplyRuleQuery';
import AdminAttackIPMonitor from './pages/AdminAttackIPMonitor';
import SSOCallback from './components/SSOCallback';
import 'bootstrap/dist/css/bootstrap.min.css';
import './Navbar.css'; // 导入导航栏样式

// 将antd Modal注册到全局，供API拦截器使用
window.antd = { Modal };

// 检查 token 是否有效
const isAuthenticated = () => {
  const authenticated = isUserAuthenticated();
  if (!authenticated) {
    // token无效或过期，断开WebSocket并清除数据
    websocketService.disconnect();
    clearAuthData();
  }
  return authenticated;
};

// 获取用户角色
const getUserRole = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  return getUserRoleFromToken();
};

// 获取账号类型 - 优先从token解析，fallback到localStorage
const getAccountType = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  return getAccountTypeFromToken();
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  // 使用单点登录检测Hook
  useSingleSignOn();

  // WebSocket自动连接逻辑
  useEffect(() => {
    const initializeWebSocket = () => {
      // 检查用户是否已认证
      if (isAuthenticated()) {
        const accountType = getAccountType();
        const token = localStorage.getItem('token');

        // 确保有token和账号类型
        if (token && accountType && websocketService.isAvailable()) {
          // 如果WebSocket没有连接且服务启用，自动连接
          if (!websocketService.isConnected() && websocketService.isEnabled) {
            websocketService.connect(accountType);
          }
        }
      }
    };

    // 页面加载时初始化WebSocket
    const timer = setTimeout(initializeWebSocket, 1000);

    // 监听localStorage变化（用户登录/登出时）
    const handleStorageChange = () => {
      initializeWebSocket();
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // 监听路由变化，确保认证用户保持WebSocket连接
  useEffect(() => {
    if (isAuthenticated()) {
      const accountType = getAccountType();
      if (accountType && !websocketService.isConnected() && websocketService.isEnabled) {
        websocketService.connect(accountType);
      }
    }
  }, [location.pathname]);

  // 需要隐藏导航栏的页面路径
  const hideNavbarPaths = ['/login', '/ram-login', '/register', '/forgot-password', '/reset-password', '/auth/callback', '/'];
  const shouldHideNavbar = hideNavbarPaths.includes(location.pathname);

  // 退出登录处理
  const handleLogout = async () => {
    try {
      // 获取当前账号类型
      const accountType = getAccountType();

      // 使用完整的登出流程
      const result = await performLogout(logoutAPI, () => websocketService.disconnect());

      if (result.success) {
        message.success(result.message);
      } else {
        message.warning(result.message);
      }

      // 根据账号类型跳转
      if (accountType === 'ram') {
        navigate('/ram-login');
      } else {
        navigate('/login');
      }

    } catch (error) {
      // 这里不应该到达，因为performLogout已经处理了所有错误
      console.error('登出处理异常:', error);
      message.error('登出处理出现异常');

      // 根据账号类型跳转
      const accountType = getAccountType();
      if (accountType === 'ram') {
        navigate('/ram-login');
      } else {
        navigate('/login');
      }
    }
  };

  return (
    <GlobalStateProvider>
      <div className="d-flex flex-column min-vh-100">
        {/* 系统更新通知 - 全局显示 */}
        <UpdateNotification />

        {!shouldHideNavbar && (
          <header>
            <nav className="navbar navbar-expand-lg">
              <div className="container-fluid">
                <Link className="navbar-brand" to="/">DDoS流量监控系统</Link>
                <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav" aria-controls="navbarNav" aria-expanded="false" aria-label="Toggle navigation">
                  <span className="navbar-toggler-icon"></span>
                </button>
                <div className="collapse navbar-collapse" id="navbarNav">
                  <ul className="navbar-nav me-auto mb-2 mb-lg-0">
                    {isAuthenticated() && getUserRole() === 'admin' && (
                      <>
                        <li className="nav-item">
                          <Link className="nav-link" to="/admin/dashboard">
                            <i className="fas fa-tachometer-alt"></i>
                            管理员首页
                          </Link>
                        </li>
                        <li className="nav-item dropdown">
                          <a className="nav-link dropdown-toggle" href="#!" id="userManagementDropdown" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                            <i className="fas fa-users"></i>
                            用户管理
                          </a>
                          <ul className="dropdown-menu" aria-labelledby="userManagementDropdown">
                            <li><Link className="dropdown-item" to="/admin/user-management">
                              <i className="fas fa-users"></i>
                              用户管理
                            </Link></li>
                            <li><Link className="dropdown-item" to="/admin/session-management">
                              <i className="fas fa-users-cog"></i>
                              会话管理
                            </Link></li>
                          </ul>
                        </li>
                        <li className="nav-item dropdown">
                          <a className="nav-link dropdown-toggle" href="#!" id="ipManagementDropdown" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                            <i className="fas fa-network-wired"></i>
                            IP管理
                          </a>
                          <ul className="dropdown-menu" aria-labelledby="ipManagementDropdown">
                            <li><Link className="dropdown-item" to="/admin/ip-allocation">
                              <i className="fas fa-network-wired"></i>
                              IP分配
                            </Link></li>
                            <li><Link className="dropdown-item" to="/admin/ip-permission-management">
                              <i className="fas fa-clipboard-check"></i>
                              IP权限申请审核
                            </Link></li>
                          </ul>
                        </li>
                        <li className="nav-item dropdown">
                          <a className="nav-link dropdown-toggle" href="#!" id="navbarDropdown" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                            <i className="fas fa-shield-alt"></i>
                            大墙操作
                          </a>
                          <ul className="dropdown-menu" aria-labelledby="navbarDropdown">
                            <li><Link className="dropdown-item" to="/admin/traffic-query">
                              <i className="fas fa-chart-line"></i>
                              IP流量查询
                            </Link></li>
                            <li><Link className="dropdown-item" to="/admin/get-set-meal">
                              <i className="fas fa-box"></i>
                              IP防护套餐查询
                            </Link></li>
                            <li><Link className="dropdown-item" to="/admin/ip-attack-query">
                              <i className="fas fa-exclamation-triangle"></i>
                              IP攻击记录查询
                            </Link></li>
                            <li><Link className="dropdown-item" to="/admin/all-servers-attack-query">
                              <i className="fas fa-clock"></i>
                              近24小时攻击记录
                            </Link></li>
                            <li><Link className="dropdown-item" to="/admin/ip-tow-lookup">
                              <i className="fas fa-search"></i>
                              IP牵引状态查询
                            </Link></li>
                            <li><Link className="dropdown-item" to="/admin/ip-tow-history-lookup">
                              <i className="fas fa-history"></i>
                              IP牵引历史查询
                            </Link></li>
                            <li><Link className="dropdown-item" to="/admin/filter-rule-query">
                              <i className="fas fa-filter"></i>
                              IP过滤规则查询
                            </Link></li>
                            <li><Link className="dropdown-item" to="/admin/apply-rule-query">
                              <i className="fas fa-cog"></i>
                              IP应用规则查询
                            </Link></li>
                          </ul>
                        </li>
                        <li className="nav-item dropdown">
                          <a className="nav-link dropdown-toggle" href="#!" id="blackWhiteListDropdown" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                            <i className="fas fa-list-alt"></i>
                            黑白名单
                          </a>
                          <ul className="dropdown-menu" aria-labelledby="blackWhiteListDropdown">
                            <li><Link className="dropdown-item" to="/admin/dynamic-blacklist-management">
                              <i className="fas fa-ban"></i>
                              动态黑名单管理
                            </Link></li>
                            <li><Link className="dropdown-item" to="/admin/static-blacklist-management">
                              <i className="fas fa-times-circle"></i>
                              静态黑名单管理
                            </Link></li>
                            <li><Link className="dropdown-item" to="/admin/static-whitelist-management">
                              <i className="fas fa-check-circle"></i>
                              静态白名单管理
                            </Link></li>
                          </ul>
                        </li>
                        <li className="nav-item dropdown">
                          <a className="nav-link dropdown-toggle" href="#!" id="xiaoqiangDropdown" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                            <i className="fas fa-fire-alt"></i>
                            小墙操作
                          </a>
                          <ul className="dropdown-menu" aria-labelledby="xiaoqiangDropdown">
                            <li><Link className="dropdown-item" to="/admin/traffic-query-xiaoqiang">
                              <i className="fas fa-chart-area"></i>
                              IP流量查询
                            </Link></li>
                            <li><Link className="dropdown-item" to="/admin/ip-connection-query">
                              <i className="fas fa-plug"></i>
                              IP连接数查询
                            </Link></li>
                            <li><Link className="dropdown-item" to="/admin/ip-attack-query-xiaoqiang">
                              <i className="fas fa-bug"></i>
                              IP攻击记录查询
                            </Link></li>
                          </ul>
                        </li>
                        <li className="nav-item dropdown">
                          <a className="nav-link dropdown-toggle" href="#!" id="ipPolicyDropdown" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                            <i className="fas fa-shield-alt"></i>
                            IP策略
                          </a>
                          <ul className="dropdown-menu" aria-labelledby="ipPolicyDropdown">
                            <li><Link className="dropdown-item" to="/admin/ip-policy-adjustment">
                              <i className="fas fa-filter"></i>
                              过滤规则
                            </Link></li>
                            <li><Link className="dropdown-item" to="/admin/application-rules">
                              <i className="fas fa-cogs"></i>
                              应用规则
                            </Link></li>
                            <li><Link className="dropdown-item" to="/admin/auto-protection">
                              <i className="fas fa-shield-alt"></i>
                              自动防护系统
                            </Link></li>
                            <li><Link className="dropdown-item" to="/admin/waf-management">
                              <i className="fas fa-shield-virus"></i>
                              WAF服务管理
                            </Link></li>
                          </ul>
                        </li>
                        <li className="nav-item">
                          <Link className="nav-link" to="/admin/logpage">
                            <i className="fas fa-file-alt"></i>
                            用户日志
                          </Link>
                        </li>
                        <li className="nav-item dropdown">
                          <a className="nav-link dropdown-toggle" href="#!" id="systemManagementDropdown" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                            <i className="fas fa-cogs"></i>
                            系统管理
                          </a>
                          <ul className="dropdown-menu" aria-labelledby="systemManagementDropdown">
                            <li><Link className="dropdown-item" to="/admin/notice-management">
                              <i className="fas fa-bullhorn"></i>
                              公告管理
                            </Link></li>
                            <li><Link className="dropdown-item" to="/admin/system-version-management">
                              <i className="fas fa-code-branch"></i>
                              系统版本管理
                            </Link></li>
                            <li><Link className="dropdown-item" to="/admin/admin-bug-report-management">
                              <i className="fas fa-bug"></i>
                              BUG反馈管理
                            </Link></li>
                            <li><Link className="dropdown-item" to="/admin/attack-ip-monitor">
                              <i className="fas fa-shield-alt"></i>
                              攻击IP监控管理
                            </Link></li>
                          </ul>
                        </li>
                        <li className="nav-item">
                          <Link className="nav-link" to="/admin/tickets">
                            <i className="fas fa-ticket-alt"></i>
                            工单管理
                          </Link>
                        </li>
                      </>
                    )}
                    {isAuthenticated() && getUserRole() === 'user' && (
                      <>
                        <li className="nav-item">
                          <Link className="nav-link" to="/user/dashboard">
                            <i className="fas fa-home"></i>
                            用户首页
                          </Link>
                        </li>
                        <NavDropdown title={<><i className="fas fa-chart-line"></i> 我的IP流量</>} id="user-traffic-dropdown">
                          <NavDropdown.Item as={Link} to="/user/traffic-query">
                            <i className="fas fa-chart-line"></i>
                            大墙流量查询
                          </NavDropdown.Item>
                          <NavDropdown.Item as={Link} to="/user/traffic-query-xiaoqiang">
                            <i className="fas fa-chart-area"></i>
                            小墙流量查询
                          </NavDropdown.Item>
                        </NavDropdown>
                        <li className="nav-item">
                          <Link className="nav-link" to="/user/ip-connection-query">
                            <i className="fas fa-plug"></i>
                            我的IP连接数
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link className="nav-link" to="/user/ip-attack-query">
                            <i className="fas fa-exclamation-triangle"></i>
                            我的IP攻击记录
                          </Link>
                        </li>
                        <li className="nav-item dropdown">
                          <a className="nav-link dropdown-toggle" href="#!" id="userIpPolicyDropdown" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                            <i className="fas fa-cogs"></i>
                            我的IP策略调整
                          </a>
                          <ul className="dropdown-menu" aria-labelledby="userIpPolicyDropdown">
                            <li><Link className="dropdown-item" to="/user/ip-policy-adjustment">
                              <i className="fas fa-filter"></i>
                              过滤规则
                            </Link></li>
                            <li><Link className="dropdown-item" to="/user/application-rules">
                              <i className="fas fa-cogs"></i>
                              应用规则
                            </Link></li>
                            <li><Link className="dropdown-item" to="/user/auto-protection">
                              <i className="fas fa-shield-alt"></i>
                              IP自动防护
                            </Link></li>
                            {getAccountType() !== 'ram' && (
                              <li>
                                <Link className="dropdown-item" to="/user/waf-management">
                                  <i className="fas fa-shield-virus"></i>
                                  WAF服务管理
                                </Link>
                              </li>
                            )}
                          </ul>
                        </li>
                        <li className="nav-item">
                          <Link className="nav-link" to="/user/ip-permissions">
                            <i className="fas fa-key"></i>
                            我的IP权限
                          </Link>
                        </li>
                        {getAccountType() !== 'ram' && (
                          <li className="nav-item">
                            <Link className="nav-link" to="/user/tickets">
                              <i className="fas fa-ticket-alt"></i>
                              我的工单
                            </Link>
                          </li>
                        )}
                        <li className="nav-item">
                          <Link className="nav-link" to="/user/settings">
                            <i className="fas fa-user-cog"></i>
                            用户设置
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link className="nav-link" to="/user/ram-account-management">
                            <i className="fas fa-users-cog"></i>
                            RAM账号管理
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link className="nav-link" to="/user/system-update-log">
                            <i className="fas fa-file-text"></i>
                            系统更新日志
                          </Link>
                        </li>
                      </>
                    )}
                    {isAuthenticated() && getUserRole() === 'wlj-user' && (
                      <>
                        <li className="nav-item">
                          <Link className="nav-link" to="/user/dashboard">
                            <i className="fas fa-home"></i>
                            用户首页
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link className="nav-link" to="/user/traffic-query">
                            <i className="fas fa-chart-line"></i>
                            我的IP流量(大墙)
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link className="nav-link" to="/user/ip-attack-query">
                            <i className="fas fa-exclamation-triangle"></i>
                            我的IP攻击记录
                          </Link>
                        </li>
                        <li className="nav-item dropdown">
                          <a className="nav-link dropdown-toggle" href="#!" id="wljUserIpPolicyDropdown" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                            <i className="fas fa-cogs"></i>
                            我的IP策略调整
                          </a>
                          <ul className="dropdown-menu" aria-labelledby="wljUserIpPolicyDropdown">
                            <li><Link className="dropdown-item" to="/user/ip-policy-adjustment">
                              <i className="fas fa-filter"></i>
                              过滤规则
                            </Link></li>
                            <li><Link className="dropdown-item" to="/user/application-rules">
                              <i className="fas fa-cogs"></i>
                              应用规则
                            </Link></li>
                            <li><Link className="dropdown-item" to="/user/auto-protection">
                              <i className="fas fa-shield-alt"></i>
                              IP自动防护
                            </Link></li>
                          </ul>
                        </li>
                        <li className="nav-item">
                          <Link className="nav-link" to="/user/ip-permissions">
                            <i className="fas fa-key"></i>
                            我的IP权限
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link className="nav-link" to="/user/settings">
                            <i className="fas fa-user-cog"></i>
                            用户设置
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link className="nav-link" to="/user/ram-account-management">
                            <i className="fas fa-users-cog"></i>
                            RAM账号管理
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link className="nav-link" to="/user/system-update-log">
                            <i className="fas fa-file-text"></i>
                            系统更新日志
                          </Link>
                        </li>
                      </>
                    )}
                    {isAuthenticated() && getUserRole() === 'pending-verification' && (
                      <>
                        <li className="nav-item">
                          <Link className="nav-link" to="/user/dashboard">
                            <i className="fas fa-home"></i>
                            用户首页
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link className="nav-link" to="/user/ip-permissions">
                            <i className="fas fa-key"></i>
                            我的IP权限
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link className="nav-link" to="/user/settings">
                            <i className="fas fa-user-cog"></i>
                            用户设置
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link className="nav-link" to="/user/ram-account-management">
                            <i className="fas fa-users-cog"></i>
                            RAM账号管理
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link className="nav-link" to="/user/system-update-log">
                            <i className="fas fa-file-text"></i>
                            系统更新日志
                          </Link>
                        </li>
                      </>
                    )}
                  </ul>
                  <ul className="navbar-nav ms-auto">
                    {isAuthenticated() && (
                      <>
                        <li className="nav-item">
                          <button className="btn btn-outline-danger" onClick={handleLogout}>
                            <i className="fas fa-sign-out-alt"></i>
                            退出登录
                          </button>
                        </li>
                      </>
                    )}
                    {!isAuthenticated() && (
                      <li className="nav-item">
                        <Link className="nav-link" to="/login">
                          <i className="fas fa-sign-in-alt"></i>
                          登录
                        </Link>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </nav>
          </header>
        )}

        <main className="flex-grow-1">
          <Routes>
            {/* 根路由，根据用户角色显示首页或跳转到对应页面 */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/ram-login" element={<RAMLogin />} />
            <Route path="/auth/callback" element={<SSOCallback />} />
            <Route path="/register" element={<ApplyAccount />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/admin/dashboard" element={<ProtectedRoute element={<AdminDashboard />} role="admin" />} />
            <Route path="/admin/logpage" element={<ProtectedRoute element={<LogsPage />} role="admin" />} />
            <Route path="/admin/get-set-meal" element={<ProtectedRoute element={<GetSetMeal />} role="admin" />} />
            <Route path="/admin/user-management" element={<ProtectedRoute element={<UserManagement />} role="admin" />} />
            <Route path="/user/dashboard" element={<ProtectedRoute element={<UserHomePage />} role="user" />} />
            <Route path="/user/traffic-query" element={<ProtectedRoute element={<UserDashboard />} role="user" />} />
            <Route path="/user/ip-permissions" element={<ProtectedRoute element={<UserIpPermissions />} role="user" />} />
            <Route path="/user/auto-protection" element={<ProtectedRoute element={<UserAutoProtection />} role="user" />} />
            <Route path="/admin/ip-allocation" element={<ProtectedRoute element={<IpAllocation />} role="admin" />} />
            <Route path="/admin/traffic-query" element={<ProtectedRoute element={<TrafficQuery />} role="admin" />} />
            <Route path="/admin/traffic-query-xiaoqiang" element={<ProtectedRoute element={<HuBeiXiaoQiangTrafficQuery />} role="admin" />} />
            <Route path="/admin/ip-connection-query" element={<ProtectedRoute element={<ConnectionQuery />} role="admin" />} />
            <Route path="/admin/ip-attack-query" element={<ProtectedRoute element={<AdminAttackQuery />} role="admin" />} />
            <Route path="/admin/all-servers-attack-query" element={<ProtectedRoute element={<AllServersAttackQuery />} role="admin" />} />
            <Route path="/admin/ip-attack-query-xiaoqiang" element={<ProtectedRoute element={<HuBeiXiaoQiangAdminAttackQuery />} role="admin" />} />
            <Route path="/admin/notice-management" element={<ProtectedRoute element={<NoticeManagement />} role="admin" />} />
            <Route path="/user/ip-connection-query" element={<ProtectedRoute element={<UserConnectionQuery />} role="user" />} />
            <Route path="/user/ip-attack-query" element={<ProtectedRoute element={<UserAttackQuery />} role="user" />} />
            <Route path="/admin/ip-policy-adjustment" element={<ProtectedRoute element={<IPPolicyAdjustment />} role="admin" />} />
            <Route path="/admin/ip-tow-lookup" element={<ProtectedRoute element={<IpTowLookup />} role="admin" />} />
            <Route path="/admin/ip-tow-history-lookup" element={<ProtectedRoute element={<IpTowHistoryLookup />} role="admin" />} />
            <Route path="/user/ip-policy-adjustment" element={<ProtectedRoute element={<IPPolicyAdjustmentUser />} role="user" />} />
            <Route path="/user/application-rules" element={<ProtectedRoute element={<UserApplicationRules />} role="user" />} />
            <Route path="/user/traffic-query-xiaoqiang" element={<ProtectedRoute element={<UserHuBeiXiaoQiangTrafficQuery />} role="user" />} />
            <Route path="/user/settings" element={<ProtectedRoute element={<UserSettings />} role="user" />} />
            <Route path="/admin/system-version-management" element={<ProtectedRoute element={<SystemVersionManagement />} role="admin" />} />
            <Route path="/admin/session-management" element={<ProtectedRoute element={<SessionManagement />} role="admin" />} />
            <Route path="/admin/ip-permission-management" element={<ProtectedRoute element={<IPPermissionManagement />} role="admin" />} />
            <Route path="/user/system-update-log" element={<ProtectedRoute element={<SystemUpdateLog />} role="user" />} />
            <Route path="/user/ram-account-management" element={<ProtectedRoute element={<RAMAccountManagement />} role="user" />} />
            <Route path="/admin/admin-bug-report-management" element={<ProtectedRoute element={<AdminBugReportManagement />} role="admin" />} />
            <Route path="/admin/application-rules" element={<ProtectedRoute element={<ApplicationRules />} role="admin" />} />
            <Route path="/admin/dynamic-blacklist-management" element={<ProtectedRoute element={<DynamicBlacklistManagement />} role="admin" />} />
            <Route path="/admin/static-blacklist-management" element={<ProtectedRoute element={<StaticBlacklistManagement />} role="admin" />} />
            <Route path="/admin/static-whitelist-management" element={<ProtectedRoute element={<StaticWhitelistManagement />} role="admin" />} />
            <Route path="/admin/auto-protection" element={<ProtectedRoute element={<AdminAutoProtection />} role="admin" />} />
            <Route path="/admin/waf-management" element={<ProtectedRoute element={<AdminWafManagement />} role="admin" />} />
            <Route path="/user/waf-management" element={<ProtectedRoute element={<WafManagement />} role="user" />} />
            <Route path="/admin/tickets" element={<ProtectedRoute element={<AdminTickets />} role="admin" />} />
            <Route path="/admin/tickets/:ticketId" element={<ProtectedRoute element={<AdminTicketDetail />} role="admin" />} />
            <Route path="/admin/filter-rule-query" element={<ProtectedRoute element={<AdminFilterRuleQuery />} role="admin" />} />
            <Route path="/admin/apply-rule-query" element={<ProtectedRoute element={<AdminApplyRuleQuery />} role="admin" />} />
            <Route path="/admin/attack-ip-monitor" element={<ProtectedRoute element={<AdminAttackIPMonitor />} role="admin" />} />
            <Route path="/user/tickets" element={<ProtectedRoute element={<UserTickets />} role="user" />} />
            <Route path="/user/tickets/:ticketId" element={<ProtectedRoute element={<UserTicketDetail />} role="user" />} />
          </Routes>
        </main>
      </div>
    </GlobalStateProvider>
  );
}

// 根路由组件，显示首页（无论是否登录）
const RootRedirect = () => {
  // 始终显示首页，不进行自动重定向
  return <HomePage />;
};

// 受保护的路由组件，根据登录状态和用户角色跳转
const ProtectedRoute = ({ element, role }) => {
  const isLoggedIn = isAuthenticated();
  const userRole = getUserRole();
  const accountType = getAccountType();

  if (!isLoggedIn) {
    return <Navigate to="/login" />;
  }

  const currentPath = window.location.pathname;

  // WAF管理页面特殊处理：只允许主账号且为普通用户访问
  if (currentPath === '/user/waf-management') {
    if (userRole !== 'user' || accountType === 'ram') {
      return <Navigate to="/" />;
    }
    return element;
  }

  // 用户工单页面特殊处理：只允许主账号访问
  if (currentPath === '/user/tickets' || currentPath.startsWith('/user/tickets/')) {
    if (accountType === 'ram') {
      return <Navigate to="/" />;
    }
    return element;
  }

  if (role && userRole !== role) {
    // 如果角色不匹配，禁止访问
    // 对于某些页面，wlj-user 和 pending-verification 也可以访问
    const allowedPagesForWljUser = ['/user/dashboard', '/user/traffic-query', '/user/ip-attack-query', '/user/ip-policy-adjustment', '/user/application-rules', '/user/ip-permissions', '/user/auto-protection', '/user/settings', '/user/system-update-log', '/user/ram-account-management'];
    const allowedPagesForPendingVerification = ['/user/dashboard', '/user/ip-permissions', '/user/settings', '/user/system-update-log', '/user/ram-account-management'];

    // wlj-user 可以访问完整的页面列表
    if (role === 'user' && userRole === 'wlj-user' && allowedPagesForWljUser.includes(currentPath)) {
      return element; // wlj-user 可以访问这些页面
    }

    // pending-verification 可以访问受限的页面列表
    if (role === 'user' && userRole === 'pending-verification' && allowedPagesForPendingVerification.includes(currentPath)) {
      return element; // pending-verification 可以访问这些页面
    }

    // RAM账号处理：如果是RAM账号且目标页面需要user角色，允许访问（除了WAF管理和工单页面）
    if (role === 'user' && accountType === 'ram' && currentPath !== '/user/waf-management' && currentPath !== '/user/tickets') {
      return element; // RAM账号可以访问user角色的页面（除了WAF管理和工单）
    }

    return <Navigate to="/" />;
  }

  return element; // 返回需要渲染的组件
};

export default App;
